import React, { useCallback, useEffect, useState } from 'react';
import { Sparkles, RefreshCw, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { leadsAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

/**
 * Phase 27.37.R — AI Lead Summary card.
 *
 * Shows the cached Claude-generated summary of a lead + the recommended
 * next step + up to 6 signal bullets. First-time render is empty with
 * a "Generate summary" CTA; subsequent renders show the last cached
 * copy with a "Refresh" button. Cached to `lead.ai_summary` on the
 * backend so we never re-bill Claude for the same lead unless the
 * user explicitly asks.
 */
export default function LeadAiSummaryCard({ leadId }) {
  const [state, setState] = useState({ loading: true, data: null, generatedAt: null });
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const r = await leadsAPI.aiSummary(leadId);
      setState({ loading: false, data: r.data.summary, generatedAt: r.data.generated_at });
    } catch (e) {
      setState({ loading: false, data: null, generatedAt: null });
      toast.error(getErrorMessage(e, 'Failed to load AI summary'));
    }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const regenerate = async () => {
    setGenerating(true);
    try {
      const r = await leadsAPI.regenerateAiSummary(leadId);
      setState({ loading: false, data: r.data.summary, generatedAt: r.data.generated_at });
      toast.success('AI summary generated');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to generate AI summary'));
    } finally {
      setGenerating(false);
    }
  };

  const s = state.data;
  const hasError = s?.error;

  return (
    <Card className="border-l-4 border-l-violet-500" data-testid="lead-ai-summary-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-violet-600" />
            <h3 className="text-xs font-semibold uppercase tracking-wider text-violet-700">
              AI Lead Summary
            </h3>
            {state.generatedAt && (
              <Badge variant="outline" className="text-[10px] text-slate-500 border-slate-200">
                {new Date(state.generatedAt).toLocaleString()}
              </Badge>
            )}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={regenerate}
            disabled={generating || state.loading}
            data-testid="lead-ai-refresh-btn"
            className="text-[11px]"
          >
            {generating ? (
              <><Loader2 size={12} className="animate-spin mr-1" /> Generating…</>
            ) : (
              <><RefreshCw size={12} className="mr-1" /> {s ? 'Refresh' : 'Generate'}</>
            )}
          </Button>
        </div>

        {state.loading ? (
          <p className="text-xs text-slate-500 py-2">Loading…</p>
        ) : !s ? (
          <div className="text-center py-6 space-y-2">
            <Sparkles size={20} className="mx-auto text-violet-400" />
            <p className="text-sm text-slate-600">No AI summary yet.</p>
            <p className="text-[11px] text-slate-500">
              Generate one to get a 10-second briefing before you reach out.
            </p>
            <Button
              onClick={regenerate}
              disabled={generating}
              size="sm"
              className="bg-violet-600 hover:bg-violet-700 mt-2"
              data-testid="lead-ai-generate-btn"
            >
              {generating ? 'Generating…' : 'Generate summary'}
            </Button>
          </div>
        ) : hasError ? (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded p-2.5">
            <AlertTriangle size={14} className="text-amber-600 mt-0.5" />
            <div className="text-xs text-amber-900">
              <strong>AI service unavailable:</strong> {s.error}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p
              className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap"
              data-testid="lead-ai-summary-text"
            >
              {s.summary}
            </p>
            {s.next_step && (
              <div className="rounded bg-emerald-50 border border-emerald-200 px-3 py-2" data-testid="lead-ai-next-step">
                <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold mb-0.5">
                  Recommended next step
                </div>
                <div className="text-sm text-emerald-900">{s.next_step}</div>
              </div>
            )}
            {Array.isArray(s.signals) && s.signals.length > 0 && (
              <div className="space-y-1" data-testid="lead-ai-signals">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  Signals worth noting
                </div>
                <ul className="space-y-1">
                  {s.signals.map((sig, i) => (
                    <li key={i} className="text-xs text-slate-700 flex items-start gap-1.5">
                      <span className="text-violet-500 mt-0.5">•</span>
                      <span>{sig}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
